/**
 * WatchPage - ESPN+ style On-Demand page
 * Replaces old Watch page with on-demand content: replays, highlights, featured videos
 * Sources: YouTube links, MP4 links, VCloud embeds from completed broadcasts
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, ChevronLeft, ChevronRight, Clock, Calendar, 
  PlayCircle, X, Radio, Volume2, VolumeX
} from 'lucide-react';
import { ASNLoader } from './ASNLoader';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const dateToUse = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? dateStr + 'T12:00:00' : dateStr;
  const date = new Date(dateToUse);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "TBD";
  const [hours, minutes] = timeStr.split(':');
  const date = new Date();
  date.setHours(parseInt(hours), parseInt(minutes));
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const extractYoutubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|live\/))([^&?\s]+)/);
  return match ? match[1] : null;
};

const getTeamName = (team) => typeof team === 'object' ? (team?.name || team?.abbreviation || 'TBD') : (team || 'TBD');
const getTeamLogo = (team) => typeof team === 'object' ? team?.logo_url : null;
const getVideoUrl = (game) => game.youtube_url || game.youtube_vod_url || game.recording_url || game.vcloud_url || '';

// ==============================
// FEATURED HERO CAROUSEL (ESPN+ Style)
// Rotating slides with crossfade, auto-cycle, dot indicators
// ==============================
const HeroSlide = ({ slide, onDemandGames, isActive, onPlay }) => {
  let heroGame = null;
  let heroTitle = slide?.title || '';
  let heroDescription = slide?.description || '';

  if (slide?.broadcast_id) {
    heroGame = onDemandGames.find(g => g.id === slide.broadcast_id);
    if (heroGame) {
      heroTitle = heroTitle || heroGame.title || `${getTeamName(heroGame.away_team)} @ ${getTeamName(heroGame.home_team)}`;
      heroDescription = heroDescription || heroGame.description || heroGame.sport || '';
    }
  }

  const videoUrl = slide?.youtube_url || (heroGame && getVideoUrl(heroGame)) || '';
  const heroYoutubeId = extractYoutubeId(videoUrl);
  const thumbnail = slide?.thumbnail_url || (heroYoutubeId ? `https://i.ytimg.com/vi/${heroYoutubeId}/maxresdefault.jpg` : heroGame?.thumbnail_url);
  const gameLink = heroGame ? `/game/${heroGame.id}` : null;

  return (
    <div
      className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
    >
      {thumbnail && (
        <img
          src={thumbnail}
          alt={heroTitle}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

      <div className="absolute inset-0 flex items-end pb-14 sm:items-center sm:pb-0">
        <div className="px-6 md:px-12 lg:px-16 max-w-xl">
          {heroGame?.sport && (
            <span className="inline-block bg-orange-500 text-black text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 mb-3">
              {heroGame.sport}
            </span>
          )}
          <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
            {heroTitle}
          </h1>
          {heroDescription && (
            <p className="text-zinc-300 text-sm md:text-base mb-6 leading-relaxed max-w-md">
              {heroDescription}
            </p>
          )}
          <div className="flex items-center gap-3">
            {videoUrl && (
              <button
                onClick={() => onPlay(videoUrl)}
                className="flex items-center gap-2 bg-white text-black font-bold text-sm uppercase px-6 py-3 hover:bg-zinc-200 transition-colors"
                data-testid="hero-play-btn"
              >
                <Play className="w-4 h-4" />
                Watch Now
              </button>
            )}
            {gameLink && (
              <Link
                to={gameLink}
                className="flex items-center gap-2 bg-white/10 text-white font-bold text-sm uppercase px-6 py-3 hover:bg-white/20 transition-colors backdrop-blur-sm"
              >
                Game Details
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedHero = ({ hero, onDemandGames }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playingUrl, setPlayingUrl] = useState('');
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Normalize: support both legacy single-item and new items[] format
  const items = hero?.items?.length > 0
    ? hero.items
    : [{ broadcast_id: hero?.broadcast_id, youtube_url: hero?.youtube_url, title: hero?.title, description: hero?.description, thumbnail_url: hero?.thumbnail_url }];

  const cycleSeconds = hero?.auto_cycle_seconds || 8;
  const hasMultiple = items.length > 1;

  // Auto-cycle timer
  useEffect(() => {
    if (!hasMultiple || playing || paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % items.length);
    }, cycleSeconds * 1000);
    return () => clearInterval(timerRef.current);
  }, [hasMultiple, playing, paused, cycleSeconds, items.length]);

  const goToSlide = (idx) => {
    setActiveIndex(idx);
    // Reset timer on manual navigation
    if (timerRef.current) clearInterval(timerRef.current);
    if (hasMultiple && !playing && !paused) {
      timerRef.current = setInterval(() => {
        setActiveIndex(prev => (prev + 1) % items.length);
      }, cycleSeconds * 1000);
    }
  };

  const handlePlay = (videoUrl) => {
    setPlayingUrl(videoUrl);
    setPlaying(true);
  };

  const playingYoutubeId = extractYoutubeId(playingUrl);

  return (
    <div
      className="relative w-full h-[320px] sm:h-[380px] md:h-[420px] lg:h-[480px] bg-black overflow-hidden"
      data-testid="featured-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {playing && playingYoutubeId ? (
        <>
          <iframe
            src={`https://www.youtube.com/embed/${playingYoutubeId}?autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1`}
            title="Featured Video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <button
            onClick={() => setMuted(!muted)}
            className="absolute bottom-4 right-4 p-2 bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors z-10"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button
            onClick={() => { setPlaying(false); setPlayingUrl(''); }}
            className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </>
      ) : (
        <>
          {items.map((slide, idx) => (
            <HeroSlide
              key={idx}
              slide={slide}
              onDemandGames={onDemandGames}
              isActive={idx === activeIndex}
              onPlay={handlePlay}
            />
          ))}

          {/* Dot indicators */}
          {hasMultiple && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20" data-testid="hero-dots">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`transition-all duration-300 rounded-full ${
                    idx === activeIndex
                      ? 'w-6 h-2 bg-white'
                      : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                  }`}
                  data-testid={`hero-dot-${idx}`}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>
          )}

          {/* Arrow controls */}
          {hasMultiple && (
            <>
              <button
                onClick={() => goToSlide((activeIndex - 1 + items.length) % items.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/30 hover:bg-black/60 text-white/70 hover:text-white transition-all backdrop-blur-sm rounded-full hidden sm:block"
                data-testid="hero-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => goToSlide((activeIndex + 1) % items.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/30 hover:bg-black/60 text-white/70 hover:text-white transition-all backdrop-blur-sm rounded-full hidden sm:block"
                data-testid="hero-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ==============================
// GAME CARD (On-Demand)
// ==============================
const GameCard = ({ game, size = 'normal' }) => {
  const awayName = getTeamName(game.away_team);
  const homeName = getTeamName(game.home_team);
  const awayLogo = getTeamLogo(game.away_team);
  const homeLogo = getTeamLogo(game.home_team);
  const videoUrl = getVideoUrl(game);
  const youtubeId = extractYoutubeId(videoUrl);
  const thumbnail = game.thumbnail_url || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : null);
  const isLarge = size === 'large';

  return (
    <Link
      to={`/game/${game.id}`}
      className={`group block flex-shrink-0 ${isLarge ? 'w-[280px] md:w-[380px]' : 'w-[220px] md:w-[300px]'}`}
      data-testid={`game-card-${game.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-zinc-900 overflow-hidden mb-2">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={game.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="flex items-center gap-4">
              {awayLogo && <img src={awayLogo} alt="" className="w-10 h-10 object-contain" />}
              <span className="text-zinc-600 text-lg font-bold">VS</span>
              {homeLogo && <img src={homeLogo} alt="" className="w-10 h-10 object-contain" />}
            </div>
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
          <PlayCircle className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Sport badge */}
        {game.sport && (
          <span className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-[9px] font-bold uppercase tracking-wider text-white px-2 py-0.5">
            {game.sport}
          </span>
        )}

        {/* Live indicator */}
        {game.is_live && (
          <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-bold uppercase px-2 py-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
          </span>
        )}

        {/* Score badge for completed */}
        {game.is_completed && (game.final_home_score != null || game.final_away_score != null) && (
          <span className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 tabular-nums">
            {game.final_away_score ?? 0} - {game.final_home_score ?? 0}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm">
          {awayLogo && <img src={awayLogo} alt="" className="w-4 h-4 object-contain" />}
          <span className="text-white font-medium truncate">{awayName}</span>
          <span className="text-zinc-600 text-xs">@</span>
          {homeLogo && <img src={homeLogo} alt="" className="w-4 h-4 object-contain" />}
          <span className="text-white font-medium truncate">{homeName}</span>
        </div>
        <div className="flex items-center gap-2 text-zinc-500 text-xs">
          {game.scheduled_date && <span>{formatDate(game.scheduled_date)}</span>}
          {game.scheduled_time && game.scheduled_time !== "00:00" && (
            <><span className="text-zinc-700">|</span><span>{formatTime(game.scheduled_time)}</span></>
          )}
        </div>
      </div>
    </Link>
  );
};

// ==============================
// VIDEO CARD (Featured Videos)
// ==============================
const VideoCard = ({ video }) => {
  const youtubeId = extractYoutubeId(video.youtube_url);
  const thumbnail = video.thumbnail_url || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : null);

  return (
    <a
      href={video.youtube_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block flex-shrink-0 w-[260px] md:w-[300px]"
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video bg-zinc-900 overflow-hidden mb-2">
        {thumbnail && (
          <img src={thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
          <PlayCircle className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {video.duration && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5">{video.duration}</span>
        )}
      </div>
      <p className="text-white text-sm font-medium truncate group-hover:text-orange-400 transition-colors">{video.title}</p>
      {video.description && <p className="text-zinc-500 text-xs truncate mt-0.5">{video.description}</p>}
    </a>
  );
};

// ==============================
// SCROLLABLE ROW
// ==============================
const ScrollRow = ({ title, children, count }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el?.removeEventListener('scroll', checkScroll);
  }, [checkScroll, count]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4 px-4 md:px-8">
        <h2 className="text-white font-bold text-base md:text-lg uppercase tracking-tight">{title}</h2>
        {(canScrollLeft || canScrollRight) && (
          <div className="flex gap-1">
            <button onClick={() => scroll(-1)} disabled={!canScrollLeft} className="p-1.5 bg-zinc-800 text-white disabled:text-zinc-600 disabled:bg-zinc-900 hover:bg-zinc-700 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => scroll(1)} disabled={!canScrollRight} className="p-1.5 bg-zinc-800 text-white disabled:text-zinc-600 disabled:bg-zinc-900 hover:bg-zinc-700 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide px-4 md:px-8 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {children}
      </div>
    </div>
  );
};

// ==============================
// MAIN WATCH PAGE
// ==============================
export const WatchPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sportFilter, setSportFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/watch/page-data`);
        setData(res.data);
      } catch (e) {
        console.error("Error fetching watch page data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><ASNLoader /></div>;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-500">Unable to load content. Please try again later.</p>
      </div>
    );
  }

  const { settings, featured_videos, sections, live_broadcasts, on_demand_games } = data;
  const heroEnabled = settings?.featured_hero?.enabled;
  const allGames = on_demand_games || [];

  // Single featured video — shown as the hero if hero is enabled
  // If there are featured_videos, the first one can also serve as hero content
  const singleFeaturedVideo = featured_videos?.[0] || null;

  // Get unique sports for filter tabs
  const sports = [...new Set(allGames.map(g => g.sport).filter(Boolean))].sort();

  // Filter on-demand games
  const filteredGames = sportFilter === 'all' ? allGames : allGames.filter(g => g.sport === sportFilter);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="watch-page">
      {/* Featured Hero */}
      {heroEnabled && settings.featured_hero && (
        <FeaturedHero hero={settings.featured_hero} onDemandGames={allGames} />
      )}

      {/* Non-hero header when no hero */}
      {!heroEnabled && (
        <div className="px-4 md:px-8 pt-8 pb-4">
          <h1 className="text-white font-bold text-2xl md:text-3xl uppercase tracking-tight" data-testid="watch-page-title">
            {settings?.on_demand_title || 'On Demand'}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">{settings?.hero_subtitle || 'Replays, highlights, and exclusive content'}</p>
        </div>
      )}

      <div className="space-y-8 pb-16">
        {/* LIVE NOW */}
        {live_broadcasts?.length > 0 && (
          <ScrollRow title="Live Now" count={live_broadcasts.length}>
            {live_broadcasts.map(game => <GameCard key={game.id} game={{...game, is_live: true}} size="large" />)}
          </ScrollRow>
        )}

        {/* CUSTOM SECTIONS */}
        {sections?.map(section => {
          if (!section.is_active) return null;
          const sectionGames = section.resolved_games || [];
          const sectionVideos = section.videos || [];
          const hasContent = sectionGames.length > 0 || sectionVideos.length > 0;
          if (!hasContent) return null;
          return (
            <ScrollRow key={section.id} title={section.title} count={sectionGames.length + sectionVideos.length}>
              {sectionGames.map(game => <GameCard key={game.id} game={game} />)}
              {sectionVideos.map(v => (
                v.recording_id ? (
                  <Link key={v.id} to={`/on-demand/${v.recording_id}`} className="group block flex-shrink-0 w-[260px] md:w-[300px]">
                    <div className="relative aspect-video bg-zinc-900 overflow-hidden mb-2">
                      {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                        <PlayCircle className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                    <p className="text-white text-sm font-medium truncate">{v.title}</p>
                  </Link>
                ) : v.youtube_url ? (
                  <VideoCard key={v.id} video={v} />
                ) : null
              ))}
            </ScrollRow>
          );
        })}

        {/* ALL ON-DEMAND GAMES */}
        {allGames.length > 0 && (
          <div className="px-4 md:px-8">
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <h2 className="text-white font-bold text-base md:text-lg uppercase tracking-tight">
                {settings?.on_demand_title || 'All Games'}
              </h2>
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide" data-testid="sport-filter">
                <button
                  onClick={() => setSportFilter('all')}
                  className={`px-3 py-1.5 text-xs font-bold uppercase whitespace-nowrap transition-colors ${
                    sportFilter === 'all' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                >
                  All
                </button>
                {sports.map(sport => (
                  <button
                    key={sport}
                    onClick={() => setSportFilter(sport)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase whitespace-nowrap transition-colors ${
                      sportFilter === sport ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {sport}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="on-demand-grid">
              {filteredGames.map(game => {
                const awayName = getTeamName(game.away_team);
                const homeName = getTeamName(game.home_team);
                const videoUrl = getVideoUrl(game);
                const youtubeId = extractYoutubeId(videoUrl);
                const thumbnail = game.thumbnail_url || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : null);
                const awayLogo = getTeamLogo(game.away_team);
                const homeLogo = getTeamLogo(game.home_team);

                return (
                  <Link key={game.id} to={`/game/${game.id}`} className="group" data-testid={`od-game-${game.id}`}>
                    <div className="relative aspect-video bg-zinc-900 overflow-hidden mb-2">
                      {thumbnail ? (
                        <img src={thumbnail} alt={game.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                          <div className="flex items-center gap-3">
                            {awayLogo && <img src={awayLogo} alt="" className="w-8 h-8 object-contain" />}
                            <span className="text-zinc-700 font-bold text-sm">VS</span>
                            {homeLogo && <img src={homeLogo} alt="" className="w-8 h-8 object-contain" />}
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                        <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {game.sport && (
                        <span className="absolute top-1.5 left-1.5 bg-black/70 text-[8px] font-bold uppercase tracking-wider text-white px-1.5 py-0.5">{game.sport}</span>
                      )}
                      {(game.final_home_score != null || game.final_away_score != null) && (
                        <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 tabular-nums">
                          {game.final_away_score ?? 0} - {game.final_home_score ?? 0}
                        </span>
                      )}
                    </div>
                    <p className="text-white text-xs font-medium truncate">{awayName} @ {homeName}</p>
                    <p className="text-zinc-600 text-[10px]">{formatDate(game.scheduled_date)}</p>
                  </Link>
                );
              })}
            </div>

            {filteredGames.length === 0 && (
              <div className="text-center py-16">
                <PlayCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No games available for this filter.</p>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {allGames.length === 0 && (!featured_videos || featured_videos.length === 0) && (!live_broadcasts || live_broadcasts.length === 0) && (
          <div className="text-center py-24 px-4">
            <Play className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-white text-xl font-bold mb-2">No Content Yet</h2>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              On-demand games and featured videos will appear here once they are added.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchPage;
